import React, { useEffect, useState, useCallback } from 'react'
import { Container, Grid, Paper, Typography, Box, Divider } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux';
import { calculateOverallAttendancePercentage } from '../../components/attendanceCalculator';
import CustomPieChart from '../../components/CustomPieChart';
import { getUserDetails } from '../../redux/userRelated/userHandle';
import styled from 'styled-components';
import SeeNotice from '../../components/SeeNotice';
import CountUp from 'react-countup';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import GroupIcon from '@mui/icons-material/Group';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { getSubjectList } from '../../redux/sclassRelated/sclassHandle';
import { motion } from 'framer-motion';
import StudentIdCard from './StudentIdCard';
import axios from 'axios';

import { Link } from 'react-router-dom';

const StudentHomePage = () => {
    const dispatch = useDispatch();

    const { userDetails, currentUser, loading, response } = useSelector((state) => state.user);
    const { subjectsList } = useSelector((state) => state.sclass);

    const [subjectAttendance, setSubjectAttendance] = useState([]);

    const classID = currentUser?.sclassName?._id

    const [hostelDetails, setHostelDetails] = useState(null);
    const [aiInsight, setAiInsight] = useState("");
    const [aiLoading, setAiLoading] = useState(true);

    useEffect(() => {
        if(currentUser && classID) {
            dispatch(getUserDetails(currentUser._id, "Student"));
            dispatch(getSubjectList(classID, "ClassSubjects"));
            fetchHostelDetails();
            fetchAiInsight();
        }
    }, [dispatch, currentUser, classID, fetchHostelDetails]);

    const fetchAiInsight = async () => {
        try {
            setAiLoading(true);
            const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/Student/ai-nudge/${currentUser._id}`);
            if (res.data && res.data.insight) {
                setAiInsight(res.data.insight);
            }
        } catch (err) {
            console.error("Failed to fetch AI insight", err);
            setAiInsight("You're on track! Keep up the good work today.");
        } finally {
            setAiLoading(false);
        }
    };

    const fetchHostelDetails = useCallback(async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/StudentHostelDetails/${currentUser._id}`);
            if (res.data && res.data.hostel) {
                // Map the room schema format to what the UI expects
                const roomData = res.data;
                const roommates = roomData.currentOccupants
                    .filter(occupant => occupant._id !== currentUser._id)
                    .map(occupant => occupant.name);
                    
                setHostelDetails({
                    hostelName: roomData.hostel.hostelName,
                    wardenName: roomData.hostel.wardenName,
                    roomNumber: roomData.roomNumber,
                    roommates: roommates
                });
            }
        } catch (err) {
            console.error(err);
        }
    }, [currentUser?._id]);

    const numberOfSubjects = subjectsList && subjectsList.length;

    useEffect(() => {
        if (userDetails) {
            setSubjectAttendance(userDetails.attendance || []);
        }
    }, [userDetails])

    const overallAttendancePercentage = calculateOverallAttendancePercentage(subjectAttendance);
    const overallAbsentPercentage = 100 - overallAttendancePercentage;

    const chartData = [
        { name: 'Present', value: overallAttendancePercentage },
        { name: 'Absent', value: overallAbsentPercentage }
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1 }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { duration: 0.5, ease: "easeOut" }
        }
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <motion.div variants={containerVariants} initial="hidden" animate="visible">
                
                {/* Greeting Header */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: '700', color: '#0f172a', letterSpacing: '-0.5px' }}>
                        Welcome back, {currentUser?.name}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ color: '#64748b', mt: 1 }}>
                        Here's what's happening with your academics today.
                    </Typography>
                </Box>
                
                {/* AR Glassmorphism AI Insight Notification */}
                <motion.div variants={itemVariants}>
                    <Box sx={{ 
                        mb: 4, 
                        p: 3, 
                        borderRadius: '16px', 
                        background: 'rgba(255, 255, 255, 0.4)', 
                        backdropFilter: 'blur(20px)', 
                        WebkitBackdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.6)',
                        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.07)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        {/* Glow effect in background */}
                        <Box sx={{
                            position: 'absolute',
                            top: '-50%',
                            left: '-10%',
                            width: '40%',
                            height: '200%',
                            background: 'linear-gradient(90deg, rgba(99,102,241,0.0) 0%, rgba(99,102,241,0.15) 50%, rgba(99,102,241,0.0) 100%)',
                            transform: 'rotate(25deg)',
                            pointerEvents: 'none',
                        }} />
                        
                        <Box sx={{ 
                            background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                            borderRadius: '12px',
                            p: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(99,102,241,0.4)',
                            zIndex: 1
                        }}>
                            <AutoAwesomeIcon sx={{ color: '#fff', fontSize: 24 }} />
                        </Box>
                        <Box sx={{ zIndex: 1 }}>
                            {aiLoading ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#a855f7', animation: 'pulse 1.5s infinite' }} />
                                    <Typography variant="body1" sx={{ color: '#334155', fontWeight: 500 }}>
                                        Updating your dashboard...
                                    </Typography>
                                </Box>
                            ) : (
                                <Typography variant="body1" sx={{ color: '#0f172a', fontWeight: 500, lineHeight: 1.5 }}>
                                    {aiInsight}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </motion.div>
                
                <Grid container spacing={4}>
                    {/* Left Column: Stats, Attendance, Hostel */}
                    <Grid item xs={12} md={8} lg={8}>
                        <Grid container spacing={3}>
                            
                            {/* Total Subjects Card */}
                            <Grid item xs={12} sm={6}>
                                <motion.div variants={itemVariants}>
                                    <StyledPaper elevation={0}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <Box>
                                                <Title>Total Subjects</Title>
                                                <Data start={0} end={numberOfSubjects || 0} duration={2} />
                                            </Box>
                                            <IconWrapper sx={{ background: '#f1f5f9', color: '#3b82f6' }}>
                                                <MenuBookIcon sx={{ fontSize: 20 }} />
                                            </IconWrapper>
                                        </Box>
                                    </StyledPaper>
                                </motion.div>
                            </Grid>
                            
                            {/* Active Assignments Card */}
                            <Grid item xs={12} sm={6}>
                                <motion.div variants={itemVariants}>
                                    <Link to="/Student/assignments" style={{ textDecoration: 'none' }}>
                                        <StyledPaper elevation={0}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <Box>
                                                    <Title>Active Assignments</Title>
                                                    <Data start={0} end={5} duration={2} /> 
                                                </Box>
                                                <IconWrapper sx={{ background: '#fef3c7', color: '#d97706' }}>
                                                    <AssignmentIcon sx={{ fontSize: 20 }} />
                                                </IconWrapper>
                                            </Box>
                                        </StyledPaper>
                                    </Link>
                                </motion.div>
                            </Grid>

                            {/* Attendance Calendar Card */}
                            <Grid item xs={12}>
                                <motion.div variants={itemVariants}>
                                    <ChartPaper elevation={0}>
                                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                            <Typography variant="h6" sx={{ fontWeight: '600', color: '#0f172a', fontSize: '1rem' }}>Attendance Overview</Typography>
                                            <Box sx={{ px: 1.5, py: 0.5, borderRadius: '6px', background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#475569', fontWeight: '500', fontSize: '0.8rem' }}>
                                                July 2026
                                            </Box>
                                        </Box>
                                        
                                        <Box sx={{ 
                                            width: '100%', 
                                            flex: 1,
                                            display: 'flex', 
                                            flexDirection: 'column',
                                            alignItems: 'center', 
                                            justifyContent: 'center',
                                            background: '#f8fafc',
                                            borderRadius: '8px',
                                            border: '1px dashed #cbd5e1',
                                            p: 4
                                        }}>
                                            <Box sx={{ 
                                                width: 48, 
                                                height: 48, 
                                                borderRadius: '50%', 
                                                background: '#f1f5f9',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                mb: 2
                                            }}>
                                                <CalendarMonthIcon sx={{ color: '#94a3b8' }} />
                                            </Box>
                                            <Typography variant="subtitle1" sx={{ color: '#334155', fontWeight: '600' }}>
                                                No Attendance Data
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                                                Attendance tracking hasn't started for this term.
                                            </Typography>
                                        </Box>
                                    </ChartPaper>
                                </motion.div>
                            </Grid>

                            {/* Hostel Details Card */}
                            <Grid item xs={12}>
                                <motion.div variants={itemVariants}>
                                    <Paper elevation={0} sx={{ 
                                        p: 4, 
                                        borderRadius: '12px', 
                                        background: '#ffffff',
                                        border: '1px solid #e2e8f0',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                                    }}>
                                        <Typography variant="h6" sx={{ mb: 3, fontWeight: '600', color: '#0f172a', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <HomeIcon sx={{ fontSize: 20, color: '#64748b' }} />
                                            Hostel Accommodation
                                        </Typography>
                                        
                                        {hostelDetails ? (
                                            <Grid container spacing={3}>
                                                <Grid item xs={12} sm={6}>
                                                    <HostelInfoBox>
                                                        <Typography variant="subtitle2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>Hostel Name</Typography>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: '600', color: '#0f172a' }}>{hostelDetails.hostelName}</Typography>
                                                    </HostelInfoBox>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <HostelInfoBox>
                                                        <Typography variant="subtitle2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>Warden</Typography>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: '600', color: '#0f172a' }}>{hostelDetails.wardenName}</Typography>
                                                    </HostelInfoBox>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <HostelInfoBox>
                                                        <Typography variant="subtitle2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>Room Number</Typography>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: '600', color: '#0f172a' }}>{hostelDetails.roomNumber}</Typography>
                                                    </HostelInfoBox>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <HostelInfoBox>
                                                        <Typography variant="subtitle2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>Roommates</Typography>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: '600', color: '#0f172a' }}>
                                                            {hostelDetails.roommates.length > 0 ? hostelDetails.roommates.join(", ") : "None"}
                                                        </Typography>
                                                    </HostelInfoBox>
                                                </Grid>
                                            </Grid>
                                        ) : (
                                            <Box sx={{ p: 3, background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1', textAlign: 'center' }}>
                                                <Typography variant="body2" sx={{ color: '#64748b' }}>No hostel room allocated yet.</Typography>
                                            </Box>
                                        )}
                                    </Paper>
                                </motion.div>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Right Column: ID Card, Timetable, Notices */}
                    <Grid item xs={12} md={4} lg={4}>
                        <motion.div variants={itemVariants} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                            <StudentIdCard currentUser={currentUser} />
                            
                            {/* Timetable Card */}
                            <Paper elevation={0} sx={{ 
                                p: 3, 
                                borderRadius: '12px', 
                                background: '#ffffff',
                                border: '1px solid #e2e8f0',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                            }}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: '600', color: '#0f172a', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CalendarMonthIcon sx={{ color: '#64748b', fontSize: 20 }} />
                                    Upcoming Classes
                                </Typography>
                                <Box sx={{ 
                                    p: 3, 
                                    borderRadius: '8px', 
                                    background: '#f8fafc',
                                    border: '1px dashed #cbd5e1',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                    minHeight: '140px'
                                }}>
                                    <Typography variant="subtitle2" sx={{ color: '#475569', fontWeight: '500' }}>
                                        No classes scheduled yet
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5, fontSize: '0.8rem' }}>
                                        Check back once the term begins.
                                    </Typography>
                                </Box>
                            </Paper>

                            {/* Notices Card */}
                            <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', flex: 1 }}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: '600', color: '#0f172a', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <NotificationsActiveIcon sx={{ color: '#64748b', fontSize: 20 }} />
                                    Latest Notices
                                </Typography>
                                <Divider sx={{ mb: 2, borderColor: '#f1f5f9' }} />
                                <Box sx={{ maxHeight: '400px', overflowY: 'auto', pr: 1 }}>
                                    <SeeNotice />
                                </Box>
                            </Paper>

                        </motion.div>
                    </Grid>
                </Grid>
            </motion.div>
        </Container>
    )
}

const StyledPaper = styled(Paper)`
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 120px;
  border-radius: 12px !important;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important;
  transition: all 0.2s ease;

  &:hover {
    border-color: #cbd5e1;
    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06) !important;
  }
`;

const ChartPaper = styled(Paper)`
  padding: 24px;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 250px;
  border-radius: 12px !important;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important;
`;

const IconWrapper = styled(Box)`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Title = styled.p`
  font-size: 0.85rem;
  margin: 0 0 8px 0;
  font-weight: 500;
  color: #64748b;
`;

const Data = styled(CountUp)`
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: -1px;
  color: #0f172a;
`;

const HostelInfoBox = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export default StudentHomePage